import type { DataGroup, TreeDataGridRow } from '@/widgets/TreeDataGrid';
import { DataGridViewType } from '@/features/DataGridTypeSwitcher';
import { DOWNLOAD_PAGE_SIZE } from '@/features/DownloadData';
import type {
  Filters,
  StringFilterParams,
  NumberFilterParams,
} from '@/features/filters';
import { getApplicableFilters, prepareFilters } from '@/features/filters';
import { prepareDateRangeValueObject } from '@/shared/api';
import { DEFAULT_PAGE_SIZE, RowHeights } from '@/shared/constants';
import { DataStatus, isFinished } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import type {
  SerializedDateRangeValueObject,
  Sorting,
  SortingItem,
} from '@/shared/types';
import { DateRangePresets, DateScale } from '@/shared/types';
import type { TotalData } from '@/shared/ui';
import * as CohortRetentionAPI from '../api/cohortRetentionAPI';
import type {
  ColumnsMeta,
  DataConfig,
  DataResponse,
  TreeDataResponse,
} from './types';
import { CohortAgeScale } from './types';

interface State {
  meta: {
    data: ColumnsMeta;
    status: DataStatus;
  };
  config: DataConfig | null;
}

const initialState: State = {
  meta: {
    status: DataStatus.idle,
    data: {
      defaultColumns: { dimensions: [], metric: '' },
      config: {},
      dimensions: [],
      metrics: [],
    },
  },
  config: null,
};

export const cohortRetentionSlice = createAppSlice({
  name: 'cohortRetention',
  initialState,
  reducers: (create) => ({
    setupColumnsMeta: create.asyncThunk(
      (_: void) => {
        return CohortRetentionAPI.getColumnsMeta();
      },
      {
        pending: (state) => {
          state.meta.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.meta.data = action.payload;
          state.meta.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.meta.status = DataStatus.error;
        },
      },
    ),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
    resetConfig: create.reducer((state) => {
      if (!state.meta.data) {
        return;
      }

      state.config = getDefaultConfig(state.meta.data);
    }),
    reorderDimensions: create.reducer<{
      id: string;
      oldIndex: number;
      newIndex: number;
    }>((state, action) => {
      if (!state.config) {
        return;
      }

      const newDimensions = state.config.dimensions.toSpliced(
        action.payload.oldIndex,
        1,
      );

      newDimensions.splice(action.payload.newIndex, 0, action.payload.id);
      state.config.dimensions = newDimensions;
    }),
    updateConfig: create.reducer<DataConfig>((state, action) => {
      state.config = action.payload;
    }),
    updateFilters: create.reducer<Filters>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.filters = action.payload;
    }),
    updateSorting: create.reducer<{ column: string; item?: SortingItem }>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        if (!action.payload.item) {
          state.config.sorting = state.config.sorting.filter((item) => {
            return item.name !== action.payload.column;
          });
        } else {
          const index = state.config.sorting.findIndex((item) => {
            return item.name === action.payload.column;
          });

          if (index !== -1) {
            state.config.sorting[index] = action.payload.item;
          } else {
            state.config.sorting.push(action.payload.item);
          }
        }
      },
    ),
    updateDimensions: create.reducer<string[]>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.dimensions = action.payload;
    }),
    updateDateScale: create.reducer<DateScale>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.dateScale = action.payload;
    }),
    updateDateRange: create.reducer<SerializedDateRangeValueObject>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.dateRange = action.payload;
      },
    ),
    updateIsForecasted: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      const isForecasted = action.payload;

      state.config.isForecasted = isForecasted;

      if (isForecasted) {
        state.config.probabilisticAttribution = false;
      }
    }),
    updateProbabilisticAttribution: create.reducer<boolean>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.probabilisticAttribution = action.payload;
    }),
    updateDataGridViewType: create.reducer<DataGridViewType>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.dataGridViewType = action.payload;
      },
    ),
    updateConfigRowHeight: create.reducer<number>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.rowHeight = action.payload;
    }),
    updateConfigColumnsWidth: create.reducer<
      { name: string; width?: number }[]
    >((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.columnsWidth = {
        ...state.config.columnsWidth,
        ...Object.fromEntries(
          action.payload.map((item) => {
            return [item.name, item.width];
          }),
        ),
      };
    }),
    updateConfigCohortAgeScale: create.reducer<CohortAgeScale>(
      (state, action) => {
        if (!state.config) {
          return;
        }

        state.config.cohortAgeScale = action.payload;
      },
    ),
    updateConfigMetric: create.reducer<string>((state, action) => {
      if (!state.config) {
        return;
      }

      state.config.metric = action.payload;
    }),
  }),
  selectors: {
    selectMetaIsLoaded: (state) => {
      return isFinished(state.meta.status);
    },
    selectMetaStatus: (state) => {
      return state.meta.status;
    },
    selectConfig: (state) => {
      return state.config;
    },
    selectMetaDimensions: (state) => {
      return state.meta.data.dimensions;
    },
    selectMetaMetrics: (state) => {
      return state.meta.data.metrics;
    },
    selectMetaConfig: (state) => {
      return state.meta.data.config;
    },
  },
});

const getDefaultConfig = (meta: ColumnsMeta): DataConfig => {
  return {
    dateRange: { preset: DateRangePresets.ALL_TIME },
    dateScale: DateScale.MONTH,
    cohortAgeScale: CohortAgeScale.THIRTY_DAYS,
    probabilisticAttribution: true,
    dimensions: meta.defaultColumns.dimensions,
    metric: meta.defaultColumns.metric,
    isForecasted: false,
    filters: [],
    sorting: [],
    rowHeight: RowHeights.normal,
    columnsWidth: {},
    metricsColoring: [],
    dataGridViewType: DataGridViewType.dataTable,
  };
};

export const getDataUpdateInfo = () => () => {
  return CohortRetentionAPI.getDataUpdateInfo();
};

interface GetTotalParams {
  dimensions: string[];
  metric: string;
  dateRange: SerializedDateRangeValueObject;
  dateScale: DateScale;
  isForecasted: boolean;
  cohortAgeScale: CohortAgeScale;
  filters: Filters;
  probabilisticAttribution: boolean;
}

export const getTotalData =
  (params: GetTotalParams): AppThunk<Promise<TotalData>> =>
  (_dispatch, getState) => {
    const state = getState();
    const allDimensions = selectMetaDimensions(state);
    const allMetrics = selectMetaMetrics(state);
    const { dimensionsFilters } = prepareFilters(
      params.filters,
      allMetrics,
      allDimensions,
    );

    return CohortRetentionAPI.getTotal({
      dimensions: params.dimensions,
      metric: params.metric,
      dateRange: prepareDateRangeValueObject(params.dateRange),
      dateScale: params.dateScale,
      probabilisticAttribution: params.probabilisticAttribution,
      isForecasted: params.isForecasted,
      cohortAgeScale: params.cohortAgeScale,
      dimensionsFilters,
    });
  };

export const getTreeData =
  (parentGroups: DataGroup[]): AppThunk<Promise<TreeDataResponse>> =>
  (_dispatch, getState) => {
    const state = getState();
    const {
      dimensions,
      metric,
      dateRange,
      dateScale,
      cohortAgeScale,
      filters,
      sorting,
      isForecasted,
      probabilisticAttribution,
    } = selectConfig(state)!;
    const allDimensions = selectMetaDimensions(state);
    const allMetrics = selectMetaMetrics(state);
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      filters,
      allMetrics,
      allDimensions,
    );

    return CohortRetentionAPI.getTreeData({
      dimensions: [dimensions[parentGroups.length]],
      metric,
      dateScale,
      dateRange: prepareDateRangeValueObject(dateRange),
      sort: sorting,
      probabilisticAttribution,
      cohortAgeScale,
      isForecasted,
      dimensionsFilters,
      metricsFilters,
      parentGroups: parentGroups,
    }).then((response) => {
      return {
        columns: response.columns,
        rows: response.rows.map((row) => {
          return {
            ...row,
            path: [
              ...parentGroups.map((parentGroup) => {
                return parentGroup.value;
              }),
              row[dimensions[parentGroups.length]],
            ],
          } as TreeDataGridRow;
        }),
      };
    });
  };

interface GetPageParams {
  dimensions: string[];
  metric: string;
  dateScale: DateScale;
  dateRange: SerializedDateRangeValueObject;
  sorting: Sorting;
  probabilisticAttribution: boolean;
  cohortAgeScale: CohortAgeScale;
  isForecasted: boolean;
  filters: Filters;
  offset?: number;
  pageSize?: number;
}

export const getPage =
  (params: GetPageParams): AppThunk<Promise<DataResponse>> =>
  (_dispatch, getState) => {
    const state = getState();
    const allDimensions = selectMetaDimensions(state);
    const allMetrics = selectMetaMetrics(state);
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      params.filters,
      allMetrics,
      allDimensions,
    );

    return CohortRetentionAPI.getData({
      dimensions: params.dimensions,
      metric: params.metric,
      dateScale: params.dateScale,
      dateRange: prepareDateRangeValueObject(params.dateRange),
      cohortAgeScale: params.cohortAgeScale,
      isForecasted: params.isForecasted,
      offset: params.offset || 0,
      sort: params.sorting,
      probabilisticAttribution: params.probabilisticAttribution,
      dimensionsFilters,
      metricsFilters,
      pageSize: params.pageSize || DEFAULT_PAGE_SIZE,
    });
  };

interface DownloadDataParams {
  dimensions: string[];
  metric: string;
  dateScale: DateScale;
  dateRange: SerializedDateRangeValueObject;
  sorting: Sorting;
  probabilisticAttribution: boolean;
  cohortAgeScale: CohortAgeScale;
  isForecasted: boolean;
  filters: Filters;
}

export const downloadData =
  (
    params: DownloadDataParams,
  ): AppThunk<
    Promise<{
      rows: DataResponse['rows'];
      total: TotalData;
      columns: string[];
    }>
  > =>
  async (dispatch) => {
    return Promise.all([
      dispatch(
        getTotalData({
          dimensions: params.dimensions,
          metric: params.metric,
          dateRange: params.dateRange,
          dateScale: params.dateScale,
          isForecasted: params.isForecasted,
          cohortAgeScale: params.cohortAgeScale,
          filters: params.filters,
          probabilisticAttribution: params.probabilisticAttribution,
        }),
      ),
      dispatch(
        getPage({
          dimensions: params.dimensions,
          metric: params.metric,
          dateScale: params.dateScale,
          dateRange: params.dateRange,
          sorting: params.sorting,
          probabilisticAttribution: params.probabilisticAttribution,
          cohortAgeScale: params.cohortAgeScale,
          isForecasted: params.isForecasted,
          filters: params.filters,
          pageSize: DOWNLOAD_PAGE_SIZE,
        }),
      ),
    ]).then(([totalResponse, rowsResponse]) => {
      return {
        rows: rowsResponse.rows,
        total: totalResponse,
        columns: rowsResponse.columns,
      };
    });
  };

interface GetStringFilterParams {
  filterName: string;
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  dateRange: SerializedDateRangeValueObject;
}

export const getStringFilterParams =
  (params: GetStringFilterParams): AppThunk<Promise<StringFilterParams>> =>
  () => {
    const { dimensionsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CohortRetentionAPI.getStringFilterParams({
      filterName: params.filterName,
      dimensionsFilters,
      dateRange: prepareDateRangeValueObject(params.dateRange),
    });
  };

interface GetNumberFilterParams {
  filterName: string;
  dimensions: string[];
  filters: Filters;
  allMetrics: string[];
  allDimensions: string[];
  dateRange: SerializedDateRangeValueObject;
  dateScale: DateScale;
  cohortAgeScale: CohortAgeScale;
  isForecasted: boolean;
  probabilisticAttribution: boolean;
}

export const getNumberFilterParams =
  (params: GetNumberFilterParams): AppThunk<Promise<NumberFilterParams>> =>
  (_dispatch, getState) => {
    const state = getState();
    const { dataGridViewType } = selectConfig(state)!;
    const { dimensionsFilters, metricsFilters } = prepareFilters(
      getApplicableFilters(params.filters, params.filterName),
      params.allMetrics,
      params.allDimensions,
    );

    return CohortRetentionAPI.getNumberFilterParams({
      dimensions: params.dimensions,
      filterName: params.filterName,
      dimensionsFilters,
      metricsFilters,
      dateRange: prepareDateRangeValueObject(params.dateRange),
      dateScale: params.dateScale,
      cohortAgeScale: params.cohortAgeScale,
      isForecasted: params.isForecasted,
      probabilisticAttribution: params.probabilisticAttribution,
      groupedData: dataGridViewType === DataGridViewType.treeDataGrid,
    });
  };

export const {
  updateConfig,
  reset,
  resetConfig,
  setupColumnsMeta,
  updateFilters,
  updateSorting,
  updateDimensions,
  updateDateScale,
  updateDateRange,
  updateIsForecasted,
  updateProbabilisticAttribution,
  updateDataGridViewType,
  updateConfigRowHeight,
  updateConfigColumnsWidth,
  updateConfigCohortAgeScale,
  updateConfigMetric,
  reorderDimensions,
} = cohortRetentionSlice.actions;
export const {
  selectConfig,
  selectMetaIsLoaded,
  selectMetaStatus,
  selectMetaDimensions,
  selectMetaMetrics,
  selectMetaConfig,
} = cohortRetentionSlice.selectors;
